import { PlusIcon, TrashIcon } from '@radix-ui/react-icons'
import { Button, Flex, IconButton, TextField } from '@radix-ui/themes'
import { useMemo, useState } from 'react'
import { RewardScheduleWithGame } from '@Generated/back-office-client/src'
import { EditDrawerFrame } from './edit-drawer-frame'
import { PrizeScheduleForm, ScheduleDetailsFormData } from './form'
import { Field, FieldError } from './form-fields'
import { useSaveScheduleContent } from './use-save-schedule-content'

type ScheduleDetailsEditDrawerProps = {
  schedule: RewardScheduleWithGame
  onClose: () => void
}

export const ScheduleDetailsEditDrawer = ({
  schedule,
  onClose,
}: ScheduleDetailsEditDrawerProps) => {
  const [form, setForm] = useState<ScheduleDetailsFormData>(() =>
    PrizeScheduleForm.toScheduleDetailsForm(schedule),
  )
  const errors = useMemo(
    () => PrizeScheduleForm.validateScheduleDetails(form, schedule.game),
    [form, schedule.game],
  )
  const { save, saveError, isSaving } = useSaveScheduleContent(schedule.id, onClose)

  return (
    <EditDrawerFrame
      title={`Edit ${schedule.name} details`}
      saveError={saveError}
      isSaving={isSaving}
      canSave={Object.keys(errors).length === 0}
      onSave={() => save(PrizeScheduleForm.toScheduleDetailsUpdate(form))}
      onClose={onClose}>
      <Field
        label="Promo text"
        hint={`One message per line; up to ${schedule.game.promoTextLimit} characters each.`}>
        <Flex direction="column" gap="2">
          {form.promoText.map((line, index) => (
            <Flex key={index} direction="column" gap="1">
              <Flex gap="2" align="center">
                <TextField.Root
                  aria-label={`Promo text ${index + 1}`}
                  style={{ flex: 1 }}
                  value={line}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      promoText: form.promoText.map((l, i) => (i === index ? e.target.value : l)),
                    })
                  }
                />
                <IconButton
                  variant="ghost"
                  color="red"
                  aria-label={`Remove promo text ${index + 1}`}
                  onClick={() =>
                    setForm({ ...form, promoText: form.promoText.filter((_, i) => i !== index) })
                  }>
                  <TrashIcon />
                </IconButton>
              </Flex>
              <FieldError errors={errors} path={`promoText.${index}`} />
            </Flex>
          ))}
        </Flex>
        <Button
          variant="soft"
          size="1"
          style={{ alignSelf: 'flex-start' }}
          onClick={() => setForm({ ...form, promoText: [...form.promoText, ''] })}>
          <PlusIcon /> Add promo text
        </Button>
      </Field>
      <Field label="Support link">
        <TextField.Root
          aria-label="Support link"
          value={form.supportLink}
          onChange={(e) => setForm({ ...form, supportLink: e.target.value })}
        />
        <FieldError errors={errors} path="supportLink" />
      </Field>
      <Field label="Rules link">
        <TextField.Root
          aria-label="Rules link"
          value={form.rulesLink}
          onChange={(e) => setForm({ ...form, rulesLink: e.target.value })}
        />
        <FieldError errors={errors} path="rulesLink" />
      </Field>
    </EditDrawerFrame>
  )
}
